import EmptyState from "@galaxy-io/dls/feedback/EmptyState";
import Icon from "@galaxy-io/dls/icons/Icon";
import Pagination, { PaginationSize } from "@galaxy-io/dls/navigation/Pagination";
import Text, { TextSize, TextVariant, TextWeight } from "@galaxy-io/dls/text/Text";
import { t } from "@galaxy-io/dls/theme/tokens/t";
import type { PeopleDirectory } from "@gtm/contracts";
import { pageNumber } from "@gtm/contracts";
import { styled } from "@linaria/react";
import { CaretRightIcon, UsersIcon } from "@phosphor-icons/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { QueryState } from "@/components/query-state";
import { DrawerSection, DrawerStack } from "@/components/workspace/drawer-content";
import { Inset } from "@/components/workspace/people-styles";
import { RelativeTime } from "@/components/workspace/relative-time";
import { PersonLink } from "@/features/people/person-link";
import { Avatar } from "@/features/people/profile-drawer";
import { apiClient, unwrap } from "@/lib/api-client";

const PAGE_SIZE = 40;
const detail = (person: PeopleDirectory["items"][number]) =>
  [person.login && `@${person.login}`, person.location].filter(Boolean).join(" · ");
const List = styled.nav`
  display: flex;
  flex-direction: column;
  min-width: 0;
  > a {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto auto;
    align-items: center;
    gap: ${t.space[12]};
    padding: ${t.space[12]};
    min-width: 0;
    text-decoration: none;
    color: inherit;
  }
  > a:not(:last-child) {
    border-bottom: 0.5px solid ${t.color.border.primary};
  }
  > a:hover {
    background: ${t.color.background.secondary};
  }
`;
const Identity = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${t.space[2]};
  min-width: 0;
`;
const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${t.space[8]};
  padding: ${t.space[8]} ${t.space[12]};
  border-top: 0.5px solid ${t.color.border.primary};
`;
/** People linked to a company, as a plain list that opens each person's drawer. */
export function CompanyPeople({
  companyId,
  page: requested,
  onPageChange,
}: {
  companyId: string;
  page?: string;
  onPageChange: (page: number) => void;
}) {
  const page = pageNumber(requested);
  const client = useQueryClient();
  const result = useQuery<PeopleDirectory>({
    queryKey: ["people", `companyId=${companyId}&page=${page}`],
    queryFn: ({ signal }) =>
      unwrap(
        apiClient().GET("/people", {
          params: { query: { companyId, page: String(page) } },
          signal,
        }),
      ),
    retry: false,
  });
  useEffect(() => {
    const invalidate = () => {
      void client.invalidateQueries({ queryKey: ["people"] });
    };
    window.addEventListener("records:changed", invalidate);
    return () => window.removeEventListener("records:changed", invalidate);
  }, [client]);
  const items = result.data?.items || [],
    total = result.data?.total || 0;
  return (
    <DrawerStack>
      <DrawerSection
        header="People"
        icon={UsersIcon}
        isFlush
        defaultIsOpen
        actions={result.data ? <Text size={TextSize.CAPTION}>{total} total</Text> : undefined}
      >
        {result.error || result.isPending ? (
          <QueryState
            error={result.error}
            loadingMessage="Loading people…"
            onRetry={() => void result.refetch()}
          />
        ) : !items.length ? (
          <Inset>
            <EmptyState
              header="No people are linked to this company yet"
              description="Link a person to this company to see them here."
            />
          </Inset>
        ) : (
          <>
            <List aria-label="People at this company">
              {items.map((person) => (
                <PersonLink key={person.id} id={person.id}>
                  <Avatar src={person.avatar} name={person.login} small />
                  <Identity>
                    <Text size={TextSize.BODY_MD} weight={TextWeight.MEDIUM} lineClamp={1}>
                      {person.name || person.login}
                    </Text>
                    {detail(person) && (
                      <Text size={TextSize.CAPTION} variant={TextVariant.SECONDARY} lineClamp={1}>
                        {detail(person)}
                      </Text>
                    )}
                  </Identity>
                  <Text size={TextSize.CAPTION} variant={TextVariant.TERTIARY}>
                    {person.latest_activity ? (
                      <RelativeTime value={person.latest_activity.at} label="Latest activity" />
                    ) : (
                      "—"
                    )}
                  </Text>
                  <Icon component={CaretRightIcon} size={16} />
                </PersonLink>
              ))}
            </List>
            {total > PAGE_SIZE && (
              <Footer>
                <Pagination
                  ariaLabel="Company people pagination"
                  total={total}
                  value={page}
                  pageSize={PAGE_SIZE}
                  onChange={onPageChange}
                  isDisabled={result.isFetching}
                  size={PaginationSize.SMALL}
                  isCompact
                  hasSummary
                  fillWidth
                />
              </Footer>
            )}
          </>
        )}
      </DrawerSection>
    </DrawerStack>
  );
}
